import type { TConstraints } from '@feathr/rachis';

import type { IGoogleAdsAdText } from './campaigns';
import type { ICreativeAttributes } from './creatives_base';
import { Creative, CreativeClass } from './creatives_base';

// =============================================================================
// Google Ads Creative Types
// =============================================================================

export interface IGoogleAdsCreative extends ICreativeAttributes {
  readonly _cls: CreativeClass.GoogleAd;
  descriptions: IGoogleAdsAdText[];
  headlines: IGoogleAdsAdText[];
}

/**
 * A Search campaign's ad: a Smart ad plus the ad group it belongs to and its own landing
 * page. The backend serialises it under the same `_cls` a Smart ad uses — see
 * `disambiguateCreative`.
 */
export interface IGoogleAdsSearchCreative extends IGoogleAdsCreative {
  /** Required at create and read-only after, so an ad can't be moved between groups. */
  readonly ad_group: string;
  /** This ad's landing page. Google requires a final URL on every Search ad. */
  destination_url: string;
}

// =============================================================================
// Google Ads Creative Classes
// =============================================================================

/**
 * GoogleAdsCreative handles Google Ads text creatives.
 * These creatives have headlines and descriptions instead of images/videos.
 */
export class GoogleAdsCreative<
  T extends IGoogleAdsCreative = IGoogleAdsCreative,
> extends Creative<T> {
  public readonly className: 'GoogleAdsCreative' | 'GoogleAdsSearchCreative' = 'GoogleAdsCreative';

  public override get constraints(): TConstraints<T> {
    return {} as TConstraints<T>;
  }

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      _cls: CreativeClass.GoogleAd,
    } as Partial<T>;
  }
}

/** One ad inside a Search campaign's ad group. Distinguished only by `ad_group`. */
export class GoogleAdsSearchCreative extends GoogleAdsCreative<IGoogleAdsSearchCreative> {
  public override readonly className = 'GoogleAdsSearchCreative';

  public override get constraints(): TConstraints<IGoogleAdsSearchCreative> {
    return {
      /*
       * Format only, not presence. `Collection.add` refuses to send a model that fails its
       * constraints, so requiring a final URL here meant an unfinished ad could not be
       * saved at all — "Save changes" on a draft died with a bare "Model is invalid". The
       * endpoint asks only for an `ad_group`, so an ad without its landing page persists
       * fine; that it is unfinished is reported by the Ads step, which is what blocks
       * publishing.
       */
      destination_url: {
        // Skipped while blank: validate.js runs `url` against an empty string otherwise.
        url: (value: unknown) =>
          value ? { message: '^Must start with http:// or https://.' } : undefined,
      },
      /*
       * Google rotates an ad's assets against each other, so a repeat wastes a slot
       * rather than adding a variant. Per field: two ads may share a headline, and a
       * headline may match a description.
       *
       * The minimum counts are not here. They count the rows that are filled in, which
       * `length` can't express, and `uniqueBy` has to stay the only validator on these
       * attributes for its per-row errors to keep lining up — see the note on
       * `GoogleAdsAdGroup.constraints`.
       */
      headlines: {
        uniqueBy: { keys: ['text'], message: '^Repeats headline %{number}.' },
      },
      descriptions: {
        uniqueBy: { keys: ['text'], message: '^Repeats description %{number}.' },
      },
    };
  }
}
